// How long a typed line will run when spoken, and what the page says about it.
// Client-safe: the composer shows the estimate while the visitor types and the
// route enforces the same cap, so both read this one file.
//
// The estimate is a straight line fitted to eleven_v4 output measured on
// 2026-10-06 (voice Juniper, mp3_44100_128, stability 0.5):
//   7 characters   0.96 s      57 characters   3.76 s
//   21 characters  1.60 s      98 characters   5.68 s
// i.e. about 0.6 s of lead-in plus 0.052 s per spoken character. The constants
// below round the slope up, so the estimate errs long. Voices differ: the
// slowest voice in the catalog (Arena) ran 2.48 s for the 21-character sample.
// The page therefore calls the figure an estimate and reports the real length
// once the clip exists.

import { TESLA_FACTS } from '@/lib/tesla-facts'
import { isAudioTag } from '@/lib/voice-moods'

/** The clip length the file is held to, in seconds. Read from tesla-facts. */
export const MAX_CLIP_SECONDS = TESLA_FACTS.audio.recommended.maxDurationSeconds

const LEAD_IN_SECONDS = 0.6
const SECONDS_PER_CHARACTER = 0.055

/** Spoken characters allowed per line: the most that fits MAX_CLIP_SECONDS at the rate above. */
export const MAX_SPOKEN_CHARS = Math.floor((MAX_CLIP_SECONDS - LEAD_IN_SECONDS) / SECONDS_PER_CHARACTER)

/** Typed characters allowed per line, tags included. Spoken length is what the cap above counts. */
export const MAX_TYPED_CHARS = 120

/** From here the page warns that a slower voice may run past the limit. */
export const WARN_AT_SECONDS = 4

const BRACKETED = /\[([^\]]*)\]/g

/**
 * The text with audio tags removed: the characters that become speech. Only
 * tags on the allow-list in lib/voice-moods.ts are removed. Any other bracketed
 * text stays, brackets included, and counts as speech, so a long line cannot be
 * wrapped in brackets to pass the length cap.
 */
export function spokenText(text: string): string {
  return text.replace(BRACKETED, (whole, inner: string) => (isAudioTag(inner) ? ' ' : whole)).replace(/\s+/g, ' ').trim()
}

/** `pace` scales the estimate for a voice that speaks slower than average (1 is average). */
export function estimateSpeechSeconds(text: string, pace = 1): number {
  const characters = spokenText(text).length
  return characters === 0 ? 0 : (LEAD_IN_SECONDS + characters * SECONDS_PER_CHARACTER) * pace
}

export type LengthState = 'empty' | 'fits' | 'tight' | 'over'

export interface LengthAssessment {
  state: LengthState
  spokenChars: number
  seconds: number
}

export function assessLength(text: string, pace = 1): LengthAssessment {
  const spokenChars = spokenText(text).length
  const seconds = estimateSpeechSeconds(text, pace)
  const state: LengthState =
    spokenChars === 0 ? 'empty' : spokenChars > MAX_SPOKEN_CHARS ? 'over' : seconds >= WARN_AT_SECONDS ? 'tight' : 'fits'
  return { state, spokenChars, seconds }
}

/** "3.2 s", one decimal. */
export function formatSeconds(seconds: number): string {
  return `${seconds.toFixed(1)} s`
}
